import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Tenant Theme Provider
 * ------------------------
 * Phase 4.6 — React runtime bridge for tenant theme activation.
 *
 * This is the first React-aware piece of the tenant theming pipeline.
 * Everything below it (contract, resolver, adapter, `ThemeEngine`,
 * runtime store) is framework-independent and stays that way; this
 * provider's only job is to wire those pieces into React's component
 * lifecycle:
 *
 *   binding + themeEngine (props)
 *     -> resolveTenantThemeBinding(binding)   [Phase 4.3 adapter]
 *     -> themeEngine.activateForTenant(binding) [Phase 4.4 ThemeEngine]
 *     -> React state (source of truth)
 *     -> TenantThemeContextStore.setContext(...) [Phase 4.5 store, synced only]
 *
 * No resolution, fallback, priority, registry, or loader logic lives
 * here — all of that is delegated to the existing adapter and engine.
 * This file only ever calls `resolveTenantThemeBinding` (never
 * `resolveTenantTheme` directly) and only ever calls
 * `themeEngine.activateForTenant` (never `activateTheme` directly),
 * per the Phase 4.6 contract.
 */
import * as React from 'react';
import { resolveTenantThemeBinding } from '../adapters/tenantThemeAdapter';
import { createTenantThemeContextStore } from '../context/tenantThemeContext';
/** Safe initial state: nothing resolved or activated yet. */
const INITIAL_STATE = {
    themeId: '',
    isLoading: false,
    fellBackToDefault: false,
    error: null,
};
/**
 * Builds a runtime state object, omitting optional keys entirely when
 * their value is `undefined` rather than assigning `undefined` to
 * them. Required under `exactOptionalPropertyTypes: true`, since an
 * optional field's type does not include `undefined` unless declared
 * that way explicitly.
 */
function buildState(input) {
    const state = {
        themeId: input.themeId,
        isLoading: input.isLoading,
        fellBackToDefault: input.fellBackToDefault,
    };
    if (input.tenantId !== undefined) {
        state.tenantId = input.tenantId;
    }
    if (input.theme !== undefined) {
        state.theme = input.theme;
    }
    if (input.error !== undefined) {
        state.error = input.error;
    }
    return state;
}
/**
 * The React runtime layer for tenant theme state. Distinct from the
 * Phase 4.5 `tenantThemeContext.ts` store: that file is a plain,
 * framework-independent in-memory record; this context is how React
 * components actually subscribe to the current tenant theme runtime
 * state. `undefined` is the "no provider above this point" sentinel,
 * used by `useTenantTheme` to throw a clear invariant error.
 */
export const TenantThemeRuntimeContext = React.createContext(undefined);
/**
 * Activates the correct theme for a tenant/customer workspace and
 * exposes the result to the React tree via `TenantThemeRuntimeContext`.
 *
 * `themeEngine` is always injected via props — this component never
 * constructs or owns a `ThemeEngine` instance, so callers remain free
 * to share a single engine across the app or scope one per test.
 */
export function TenantThemeProvider({ binding, themeEngine, children, }) {
    const [state, setState] = React.useState(INITIAL_STATE);
    // Phase 4.5 store instance. Created once per provider instance (not
    // a singleton, per that file's own factory contract) and used
    // write-only, as a synchronization target — never read back into
    // React state, per the Phase 4.6 requirement that React state stays
    // the source of truth.
    const contextStoreRef = React.useRef(createTenantThemeContextStore());
    React.useEffect(() => {
        let isCancelled = false;
        // No tenant binding yet: this provider must stay mounted (future
        // tenant theming still needs the context in place) but must NOT
        // activate anything. Activation ownership for the no-tenant case
        // belongs solely to `ThemeProvider`'s `initialize()` call — calling
        // `activateForTenant(null)` here as well caused two concurrent
        // writers to the shared `themeEngine`/`themeState` singleton.
        if (!binding) {
            setState(() => buildState({
                themeId: '',
                isLoading: false,
                fellBackToDefault: false,
                error: null,
            }));
            return;
        }
        // Defensive: this provider must never throw from a malformed
        // binding, even though `resolveTenantThemeBinding` and
        // `activateForTenant` already guarantee they won't throw for that
        // reason on their own.
        let resolved;
        try {
            resolved = resolveTenantThemeBinding(binding);
        }
        catch {
            resolved = { resolvedThemeId: 'default' };
        }
        setState((previous) => buildState({
            ...previous,
            themeId: resolved.resolvedThemeId,
            tenantId: resolved.tenantId,
            isLoading: true,
            error: null,
        }));
        void (async () => {
            try {
                const result = await themeEngine.activateForTenant(binding);
                if (isCancelled) {
                    return;
                }
                setState(buildState({
                    themeId: result.theme.manifest.id,
                    tenantId: resolved.tenantId,
                    theme: result.theme,
                    isLoading: false,
                    fellBackToDefault: result.fellBackToDefault,
                    error: null,
                }));
                if (resolved.tenantId) {
                    try {
                        contextStoreRef.current.setContext(resolved.tenantId, result.theme.manifest.id);
                    }
                    catch {
                        // Synchronization is best-effort; the store is not the
                        // source of truth, so a failure here must not affect the
                        // React state already committed above.
                    }
                }
            }
            catch (error) {
                if (isCancelled) {
                    return;
                }
                setState((previous) => ({
                    ...previous,
                    isLoading: false,
                    error: error instanceof Error ? error : new Error(String(error)),
                }));
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [binding, themeEngine]);
    return (_jsx(TenantThemeRuntimeContext.Provider, { value: state, children: children }));
}
